import { ClientDto, ProjectDto } from '../../../../api';
import { useForm, FormProvider, NestedValue, SubmitHandler } from 'react-hook-form';
import { Grid, Button } from '@mui/material';
import { TextField, CheckBox, SelectionBox } from '../../../../components/form/react-hook-form-components';
import { ComboBoxOption } from '../../../../components/form/controls/ComboBox';
import useFormStyles from './../../../../styles/useFormStyles';
import { getComboBoxOptions } from '../../../../utils/Utils';
import useClients from '../../../../hooks/useClients';

interface IProjectFormProps {
  onSubmit: (values: ProjectDto) => void;
  onCancel: () => void;
  client?: ClientDto;
  project?: ProjectDto;
  projectNameSuggestion?: string;
}

interface IProjectFormFields {
  client: NestedValue<ComboBoxOption> | null;
  projectID: number;
  projectName: string | null;
  description: string | null;
  isBillable: boolean;
  isActive: boolean;
  kCode?: number | null;
}

/**
 * The form for creating and updating projects
 */
function ProjectForm(props: IProjectFormProps) {
  const classes = useFormStyles();
  const { onSubmit, onCancel, client, project, projectNameSuggestion } = props;
  const { data: clients } = useClients();

  const methods = useForm<IProjectFormFields>({
    defaultValues: {
      client: getSelectedClientOption(),
      projectID: project?.id,
      projectName: project?.name ?? projectNameSuggestion,
      description: project?.description,
      isBillable: project?.isBillable ?? false,
      isActive: project?.isActive ?? false,
      kCode: project?.kCode ?? null,
    },
  });

  const { handleSubmit } = methods;

  /**
   * Gets the combo box option selected initially
   * @returns client combo box option
   */
  function getSelectedClientOption() {
    if (client === undefined && project === undefined) return null;

    const option = {
      label: client?.name ?? project?.clientName,
      value: client?.id ?? project?.clientID,
    } as ComboBoxOption;

    return option;
  }

  /**
   * Submit form data
   * @param data form fields input data
   */
  const submit: SubmitHandler<IProjectFormFields> = data => {
    onSubmit({
      clientID: data.client?.value ?? null,
      id: data.projectID,
      name: data.projectName,
      description: data.description,
      isBillable: data.isBillable,
      isActive: data.isActive,
      kCode: data.kCode ?? null,
      clientName: null,
      employees: null,
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)}>
        <Grid container spacing={1.5} width="25rem">
          {!project && !client && (
            <Grid item xs={12}>
              <SelectionBox name={'client'} label={'Client'} options={getComboBoxOptions(clients)} required />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField name="projectName" label="Project Name" required autoFocus={Boolean(project)} />
          </Grid>

          <Grid item xs={12}>
            <TextField name="description" label="Project Description" multiline />
          </Grid>

          <Grid item xs={12} height={35}>
            <CheckBox name="isBillable" label="Billable" />
          </Grid>

          <Grid item xs={12}>
            <CheckBox name="isActive" label={'Active  '} />
          </Grid>

          <Grid item marginBottom={1.5}>
            <TextField name={'kCode'} label={'K Code'} numericInput />
          </Grid>

          <Grid item container direction="row" justifyContent="end" spacing={1}>
            <Grid item>
              <Button variant="contained" size="large" type="submit" className={classes.submitButton}>
                {project ? 'Update' : 'Create'}
              </Button>
            </Grid>

            <Grid item>
              <Button variant="outlined" color="neutral" size="large" onClick={onCancel}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}

export default ProjectForm;
